"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { createClient } from "@/lib/supabase/client";

// Types
export interface VenueData {
  id: string;
  name: string;
  slug: string;
  address?: string;
  city?: string;
  country?: string;
  timezone?: string;
  currency?: string;
  settings?: Record<string, any>;
  status_id: number;
}

export interface Studio {
  id: string;
  name: string;
  description?: string;
  capacity?: number;
  venue_id: string;
  status_id: number;
}

export interface Show {
  id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  studio_id: string;
  venue_id: string;
  status_id: number;
}

export interface VenueContextData {
  // Core venue data
  venue: VenueData | null;
  studios: Studio[];
  shows: Show[];

  // Loading states
  isLoadingVenue: boolean;
  isLoadingStudios: boolean;
  isLoadingShows: boolean;

  // Error states
  venueError: string | null;
  studiosError: string | null;
  showsError: string | null;

  // Cache management
  cacheTimestamp: number | null;

  // Actions
  loadVenueData: (venueId: string) => Promise<void>;
  refreshVenueData: () => Promise<void>;
  clearCache: () => void;
}

const VenueContext = createContext<VenueContextData | null>(null);

// Session storage keys
const getStorageKey = (venueId: string, type: string) =>
  `onair_venue_${venueId}_${type}`;

interface VenueProviderProps {
  children: ReactNode;
  venueId: string;
}

export function VenueProvider({ children, venueId }: VenueProviderProps) {
  const [venue, setVenue] = useState<VenueData | null>(null);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [shows, setShows] = useState<Show[]>([]);

  const [isLoadingVenue, setIsLoadingVenue] = useState(false);
  const [isLoadingStudios, setIsLoadingStudios] = useState(false);
  const [isLoadingShows, setIsLoadingShows] = useState(false);

  const [venueError, setVenueError] = useState<string | null>(null);
  const [studiosError, setStudiosError] = useState<string | null>(null);
  const [showsError, setShowsError] = useState<string | null>(null);

  const [cacheTimestamp, setCacheTimestamp] = useState<number | null>(null);

  const supabase = createClient();

  // Load data from sessionStorage on mount
  useEffect(() => {
    if (!venueId) return;

    try {
      const cachedVenue = sessionStorage.getItem(
        getStorageKey(venueId, "venue"),
      );
      const cachedStudios = sessionStorage.getItem(
        getStorageKey(venueId, "studios"),
      );
      const cachedShows = sessionStorage.getItem(
        getStorageKey(venueId, "shows"),
      );
      const cachedTimestamp = sessionStorage.getItem(
        getStorageKey(venueId, "timestamp"),
      );

      if (cachedVenue) {
        setVenue(JSON.parse(cachedVenue));
      }
      if (cachedStudios) {
        setStudios(JSON.parse(cachedStudios));
      }
      if (cachedShows) {
        setShows(JSON.parse(cachedShows));
      }
      if (cachedTimestamp) {
        setCacheTimestamp(Number(cachedTimestamp));
      }

      // Check if cache is stale (older than 1 second for admin portals)
      const timestamp = cachedTimestamp ? Number(cachedTimestamp) : 0;
      const cacheAge = Date.now() - timestamp;
      const cacheMaxAge = 1 * 1000; // 1 second

      if (!cachedVenue || cacheAge > cacheMaxAge) {
        // Load fresh data if no cache or cache is stale
        loadVenueData(venueId);
      }
    } catch (error) {
      console.error("Error loading cached venue data:", error);
      // Fallback to fresh data if cache is corrupted
      loadVenueData(venueId);
    }
  }, [venueId]);

  // Save to sessionStorage whenever data changes
  useEffect(() => {
    if (venue && venueId) {
      sessionStorage.setItem(
        getStorageKey(venueId, "venue"),
        JSON.stringify(venue),
      );
    }
  }, [venue, venueId]);

  useEffect(() => {
    if (venueId) {
      sessionStorage.setItem(
        getStorageKey(venueId, "studios"),
        JSON.stringify(studios),
      );
    }
  }, [studios, venueId]);

  useEffect(() => {
    if (venueId) {
      sessionStorage.setItem(
        getStorageKey(venueId, "shows"),
        JSON.stringify(shows),
      );
    }
  }, [shows, venueId]);

  useEffect(() => {
    if (cacheTimestamp && venueId) {
      sessionStorage.setItem(
        getStorageKey(venueId, "timestamp"),
        cacheTimestamp.toString(),
      );
    }
  }, [cacheTimestamp, venueId]);

  const loadVenueData = async (targetVenueId: string) => {
    // Load venue data
    setIsLoadingVenue(true);
    setVenueError(null);
    try {
      const { data: venueData, error: venueErr } = await supabase
        .from("venues")
        .select("*")
        .eq("id", targetVenueId)
        .single();

      if (venueErr) throw venueErr;
      setVenue(venueData);
    } catch (error: any) {
      console.error("Error loading venue:", error);
      setVenueError(error.message || "Failed to load venue");
    } finally {
      setIsLoadingVenue(false);
    }

    // Load studios
    setIsLoadingStudios(true);
    setStudiosError(null);
    try {
      const { data: studiosData, error: studiosErr } = await supabase
        .from("studios")
        .select("*")
        .eq("venue_id", targetVenueId)
        .order("name");

      if (studiosErr) throw studiosErr;
      setStudios(studiosData || []);
    } catch (error: any) {
      console.error("Error loading studios:", error);
      setStudiosError(error.message || "Failed to load studios");
    } finally {
      setIsLoadingStudios(false);
    }

    // Load shows
    setIsLoadingShows(true);
    setShowsError(null);
    try {
      const { data: showsData, error: showsErr } = await supabase
        .from("shows")
        .select(
          `
          *,
          show_infos (
            name,
            subtitle,
            desc,
            lang
          )
        `,
        )
        .eq("venue_id", targetVenueId)
        .order("id");

      if (showsErr) throw showsErr;
      setShows(showsData || []);
    } catch (error: any) {
      console.error("Error loading shows:", error);
      setShowsError(error.message || "Failed to load shows");
    } finally {
      setIsLoadingShows(false);
    }

    // Update cache timestamp
    setCacheTimestamp(Date.now());
  };

  const refreshVenueData = async () => {
    if (!venueId) return;
    await loadVenueData(venueId);
  };

  const clearCache = () => {
    if (!venueId) return;

    // Clear state
    setVenue(null);
    setStudios([]);
    setShows([]);
    setCacheTimestamp(null);

    // Clear sessionStorage
    sessionStorage.removeItem(getStorageKey(venueId, "venue"));
    sessionStorage.removeItem(getStorageKey(venueId, "studios"));
    sessionStorage.removeItem(getStorageKey(venueId, "shows"));
    sessionStorage.removeItem(getStorageKey(venueId, "timestamp"));
  };

  const contextValue: VenueContextData = {
    venue,
    studios,
    shows,
    isLoadingVenue,
    isLoadingStudios,
    isLoadingShows,
    venueError,
    studiosError,
    showsError,
    cacheTimestamp,
    loadVenueData,
    refreshVenueData,
    clearCache,
  };

  return (
    <VenueContext.Provider value={contextValue}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenue() {
  const context = useContext(VenueContext);
  if (!context) {
    throw new Error("useVenue must be used within a VenueProvider");
  }
  return context;
}

// Convenient hooks for specific data
export function useVenueData() {
  const { venue, isLoadingVenue, venueError } = useVenue();
  return { venue, isLoading: isLoadingVenue, error: venueError };
}

export function useStudios() {
  const { studios, isLoadingStudios, studiosError } = useVenue();
  return { studios, isLoading: isLoadingStudios, error: studiosError };
}

export function useShows() {
  const { shows, isLoadingShows, showsError } = useVenue();
  return { shows, isLoading: isLoadingShows, error: showsError };
}

// Hook to get shows for a specific studio
export function useStudioShows(studioId?: string) {
  const { shows, isLoadingShows, showsError } = useVenue();

  const studioShows = studioId
    ? shows.filter((show) => show.studio_id === studioId)
    : shows;

  return {
    shows: studioShows,
    isLoading: isLoadingShows,
    error: showsError,
  };
}

// Hook to check if venue data is cached and fresh
export function useVenueCache() {
  const { cacheTimestamp, refreshVenueData, clearCache } = useVenue();

  const isCached = cacheTimestamp !== null;
  const cacheAge = cacheTimestamp ? Date.now() - cacheTimestamp : 0;
  const isStale = cacheAge > 1 * 1000; // 1 second

  return {
    isCached,
    cacheAge,
    isStale,
    refresh: refreshVenueData,
    clear: clearCache,
  };
}
